import { ref, computed } from "vue";
import { useStore } from "@/store";
const store = useStore();
const activeRowModal: any = ref({ data: null });
import { GeneralActionTypes } from "@/store/modules/general/action-types";
import i18n from '@/localization/i18n';
const { t } = i18n.global;

function escapeHtml(text: string): string {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

declare global {
  interface Window {
    AGSC_toggleSummaryApps?: (el: HTMLElement, event?: Event) => void;
  }
}

if (typeof window !== "undefined") {
  window.AGSC_toggleSummaryApps = (el: HTMLElement, event?: Event) => {
    try {
      if (event) {
        event.stopPropagation();
      }
      
      const ul = el.closest('ul');
      if (!ul) return;

      const fullDataEncoded = ul.getAttribute('data-full-data') || el.getAttribute('data-full-data') || '';
      const fullData = fullDataEncoded ? JSON.parse(decodeURIComponent(fullDataEncoded)) : [];
      const expanded = ul.getAttribute('data-expanded') === '1';

      const summaryEventEntityData: any = store.state.general.summaryEventEntityData || {};
      const levelOptions = summaryEventEntityData?.positions || [];
      const publisherLevelOptions = summaryEventEntityData?.publisherPositions || [];
      
      const getLevelText = (levelValue: string, elementName: string) => {
        if (levelValue === null || levelValue === undefined) return '-';
        const options = (elementName && elementName.includes('publishers.publisher')) 
          ? publisherLevelOptions 
          : levelOptions;
        
        const level = options.find((opt: any) => {
          return String(opt.value) === String(levelValue);
        });
        return level ? level.text : String(levelValue);
      };

      if (!expanded) {
        const html =
          fullData.map((item: any) => {
            const levelText = getLevelText(item.level, item.elementName);
            const levelBlock = `<span style="background:#C5DBF3;color:#0055A6;padding:8px;font-size:10px;font-weight:600;display:flex;align-items:center;justify-content:center;width:150px;border-radius:0 5px 5px 0;margin:-2px 0 -2px 0;border-left:1px solid #BDD0E0;text-transform:uppercase;">${escapeHtml(levelText)}</span>`;
            return `<li style="list-style:none;border:1px solid #BDD0E0;border-radius:6px;padding:2px 0 2px 12px;margin-bottom:8px;display:flex;justify-content:space-between;align-items:stretch;background:transparent;">
              <span style="color:#01172F;font-size:0.8rem;flex:1;display:flex;align-items:center;">${escapeHtml(item.elementName)}</span>
              ${levelBlock}
            </li>`;
          }).join('') +
          `<li style="list-style:none;margin-bottom:8px;">
             <a href="#" style="color:#5d5f62;font-weight:700;text-decoration:none;"
                onclick="AGSC_toggleSummaryApps(this, event);return false;">Show less</a>
           </li>`;
        ul.innerHTML = html;
        ul.setAttribute('data-expanded', '1');
      } else {
        const visible = fullData.slice(0, 2);
        const hiddenCount = Math.max(0, fullData.length - 2);

        const visibleLis = visible.map((item: any) => {
          const levelText = getLevelText(item.level, item.elementName);
          const levelBlock = `<span style="background:#C5DBF3;color:#0055A6;padding:8px;font-size:10px;font-weight:600;display:flex;align-items:center;justify-content:center;width:150px;border-radius:0 5px 5px 0;margin:-2px 0 -2px 0;border-left:1px solid #BDD0E0;text-transform:uppercase;">${escapeHtml(levelText)}</span>`;
          return `<li style="list-style:none;border:1px solid #BDD0E0;border-radius:6px;padding:2px 0 2px 12px;margin-bottom:8px;display:flex;justify-content:space-between;align-items:stretch;background:transparent;">
            <span style="color:#01172F;font-size:0.8rem;flex:1;display:flex;align-items:center;">${escapeHtml(item.elementName)}</span>
            ${levelBlock}
          </li>`;
        }).join('');
        
        const hiddenIndicator = hiddenCount > 0
          ? `<li style="list-style:none;margin-bottom:8px;">
               <a href="#" class="more-badge"
                  style="color:#5d5f62;font-weight:700;text-decoration:none;"
                  data-full-data="${fullDataEncoded}"
                  onclick="AGSC_toggleSummaryApps(this, event);return false;">+${hiddenCount} more</a>
             </li>`
          : '';
        ul.innerHTML = `${visibleLis}${hiddenIndicator}`;
        ul.setAttribute('data-expanded', '0');
      }
    } catch {
      console.error('Failed to toggle summary apps');
    }
  };
}

const data = {
    filter: {
        title: "",
        pageName: "",
        genre: "",
        ignoreSaveFilter: true,
        noDateSelect: true,
        hideFilter: false,
        filters: [
        ],
    },
    header: {
        navigation: [

        ],
        buttonPosition: "pr-4 justify-content-between",
        buttons: [

        ],
    },
    component: {
        type: "ag-table",
        options: {
            columns: [
                {
                    label: "",
                    field: "dragHandle",
                    sortable: false,
                    width: "10px",
                    style: { placeItems: 'center' },
                    display: () => {
                        return `<i class="ag-icon ag-icon_sort_outline drag-handle" style="color: #999; cursor: move;"></i>`;
                    },
                },
                {
                    label: () => t('ruleName').toUpperCase(),
                    field: "ruleName",
                    sortable: false,
                    display: (row: any) => {
                        const summaryEventEntityData: any = store.state.general.summaryEventEntityData;
                        const elementCategories = summaryEventEntityData?.elementCategories || [];
                        
                        const getEventTypeText = (eventTypeValue: string) => {
                            const eventType = elementCategories.find((opt: any) => opt.value === eventTypeValue);
                            return eventType ? eventType.text : eventTypeValue;
                        };
                        
                        const eventTypeValue = row.eventType ?? "";
                        const eventTypeText = eventTypeValue ? getEventTypeText(eventTypeValue) : "";
                        const ruleName = row.ruleName ?? "";
                        const statusChip = row.enabled
                            ? "<span style='width: fit-content;background:#35B90733;color:#31AA06;border-radius:16px;padding:1px 8px;font-weight:700;font-size:10px;display:inline-flex;align-items:center;'><span style='width:6px;height:6px;background:#31AA06;border-radius:50%;display:inline-block;margin-right:3px;'></span>On</span>"
                            : "<span style='width:fit-content;background:#D0D7E0;color:#838A92;border-radius:16px;padding:1px 8px;font-weight:700;font-size:10px;display:inline-flex;align-items:center;'><span style='width:6px;height:6px;background:#838A92;border-radius:50%;display:inline-block;margin-right:3px;'></span>Off</span>";
                        
                        if (eventTypeText) {
                            return `<div style="display:flex;flex-direction:column;gap:4px;">
                                <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                                    <span style="font-weight:bold;color:#01172F;">${eventTypeText}</span>
                                    ${statusChip}
                                </div>
                                <span style="color:#01172F;">${ruleName}</span>
                            </div>`;
                        } else {
                            return `<div style="display:flex;flex-direction:column;gap:4px;">
                                <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                                    <span style="font-weight:bold;color:#01172F;">${ruleName}</span>
                                    ${statusChip}
                                </div>
                            </div>`;
                        }
                    },
                    width: "22%",
                },
                {
                    label: () => t('groupBy').toUpperCase(),
                    field: "groupByFields",
                    sortable: true,
                    jsSort: true,
                    display: (row: any) => {
                        const groupByFields = row.groupByFields || [];
                        const filteredFields = groupByFields.filter((g: any) => g.elementName);
                        
                        if (filteredFields.length === 0) return '';

                        const summaryEventEntityData: any = store.state.general.summaryEventEntityData;
                        const levelOptions = summaryEventEntityData?.positions || [];
                        const publisherLevelOptions = summaryEventEntityData?.publisherPositions || [];
                        
                        const getLevelText = (levelValue: string, elementName: string) => {
                            if (levelValue === null || levelValue === undefined) return '-';
                            const options = (elementName && elementName.includes('publishers.publisher')) 
                                ? publisherLevelOptions 
                                : levelOptions;
                            
                            const level = options.find((opt: any) => {
                                return String(opt.value) === String(levelValue);
                            });
                            return level ? level.text : String(levelValue);
                        };

                        const visible = filteredFields.slice(0, 2);
                        const hidden = filteredFields.slice(2);
                        
                        // Encode full data including levels
                        const fullDataEncoded = encodeURIComponent(JSON.stringify(filteredFields));

                        const visibleLis = visible.map((item: any) => {
                            const levelText = getLevelText(item.level, item.elementName);
                            const levelBlock = `<span style="background:#C5DBF3;color:#0055A6;padding:8px;font-size:10px;font-weight:600;display:flex;align-items:center;justify-content:center;width:150px;border-radius:0 5px 5px 0;margin:-2px 0 -2px 0;border-left:1px solid #BDD0E0;text-transform:uppercase;">${escapeHtml(levelText)}</span>`;
                            return `<li style="list-style:none;border:1px solid #BDD0E0;border-radius:6px;padding:2px 0 2px 12px;margin-bottom:8px;display:flex;justify-content:space-between;align-items:stretch;background:transparent;">
                                <span style="color:#01172F;font-size:0.8rem;flex:1;display:flex;align-items:center;">${escapeHtml(item.elementName)}</span>
                                ${levelBlock}
                            </li>`;
                        }).join("");
                        
                        const hiddenIndicator = hidden.length > 0
                            ? `<li style="list-style:none;margin-bottom:8px;">
                                <a href="#" class="more-badge"
                                    style="color:#5d5f62; font-weight:700; text-decoration:none;"
                                    data-full-data="${fullDataEncoded}"
                                    onclick="AGSC_toggleSummaryApps(this, event);return false;">+${hidden.length} more</a>
                            </li>`
                            : "";

                        return `<ul data-full-data="${fullDataEncoded}" data-expanded="0" style="padding:0; margin:0; color:#01172F;">${visibleLis}${hiddenIndicator}</ul>`;
                    },
                    width: "50%",
                },
                {
                    label: () => t('conditions').toUpperCase(),
                    field: "conditions",
                    sortable: true,
                    jsSort: true,
                    display: (row: any) => {
                        const conditions = row.conditions ?? [];
                        const filteredConditions = conditions.filter((condition: any) => 
                            condition.elementName || condition.condition || condition.value || condition.level
                        );
                        
                        if (filteredConditions.length === 0) {
                            return `
                                <span style="
                                    display: inline-flex;
                                    align-items: center;
                                    gap: 5px;
                                    font-size: 11px;
                                    color: #9aabb8;
                                    padding: 2px 8px;
                                    border: 1px dashed #CBD5E1;
                                    border-radius: 20px;
                                    opacity: 0.5;
                                    background: transparent;
                                ">
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <line x1="4" y1="6" x2="20" y2="6"/>
                                        <line x1="4" y1="12" x2="14" y2="12"/>
                                        <line x1="4" y1="18" x2="9" y2="18"/>
                                        <line x1="17" y1="13" x2="22" y2="18"/>
                                    </svg>
                                    <span>${t('noCondition')}</span>
                                </span>
                            `;
                        }

                        const summaryEventEntityData: any = store.state.general.summaryEventEntityData;
                        const operatorOptions = summaryEventEntityData?.conditions || [];
                        const levelOptions = summaryEventEntityData?.positions || [];
                        const publisherLevelOptions = summaryEventEntityData?.publisherPositions || [];
                        
                        const getOperatorText = (operatorValue: string) => {
                            const operator = operatorOptions.find((opt: any) => opt.value === operatorValue);
                            return operator ? operator.text : operatorValue;
                        };
                        
                        const getLevelText = (levelValue: string, elementName: string) => {
                            if (levelValue === null || levelValue === undefined) return '-';
                            const options = (elementName && elementName.includes('publishers.publisher')) 
                                ? publisherLevelOptions 
                                : levelOptions;
                            
                            const level = options.find((opt: any) => {
                                return String(opt.value) === String(levelValue);
                            });
                            return level ? level.text : String(levelValue);
                        };

                        const tooltipTable = `
                            <table style="border-collapse: collapse; width: 100%; font-size: 12px;">
                                <thead>
                                    <tr style="background-color: #f5f5f5;">
                                        <th style="border-bottom: 1px solid #E5E7EB; border-right: 1px solid #F0F0F0; padding: 10px 12px; text-align: left; font-weight: 600; white-space: nowrap; width: 40%; color: #6B7280; font-size: 11px;">Property</th>
                                        <th style="border-bottom: 1px solid #E5E7EB; border-right: 1px solid #F0F0F0; padding: 10px 12px; text-align: left; font-weight: 600; white-space: nowrap; width: 20%; color: #6B7280; font-size: 11px;">Operator</th>
                                        <th style="border-bottom: 1px solid #E5E7EB; border-right: 1px solid #F0F0F0; padding: 10px 12px; text-align: left; font-weight: 600; white-space: nowrap; width: 25%; color: #6B7280; font-size: 11px;">Value</th>
                                        <th style="border-bottom: 1px solid #E5E7EB; padding: 10px 12px; text-align: left; font-weight: 600; white-space: nowrap; width: 15%; color: #6B7280; font-size: 11px;">Level</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${filteredConditions.map((condition: any, index: number) => {
                                        const operatorText = condition.condition ? getOperatorText(condition.condition) : '-';
                                        const levelText = condition.level ? getLevelText(condition.level, condition.elementName) : '-';
                                        const isLast = index === filteredConditions.length - 1;
                                        return `
                                        <tr style="background-color: #fff;">
                                            <td style="border-bottom: ${isLast ? 'none' : '1px solid #F0F0F0'}; border-right: 1px solid #F0F0F0; padding: 10px 12px; width: 40%; color: #374151;">${escapeHtml(condition.elementName || '-')}</td>
                                            <td style="border-bottom: ${isLast ? 'none' : '1px solid #F0F0F0'}; border-right: 1px solid #F0F0F0; padding: 10px 12px; width: 20%; color: #374151;">${escapeHtml(operatorText)}</td>
                                            <td style="border-bottom: ${isLast ? 'none' : '1px solid #F0F0F0'}; border-right: 1px solid #F0F0F0; padding: 10px 12px; width: 25%; color: #374151; font-weight: 500;">${escapeHtml(condition.value || '-')}</td>
                                            <td style="border-bottom: ${isLast ? 'none' : '1px solid #F0F0F0'}; padding: 10px 12px; width: 15%; color: #374151;">${escapeHtml(levelText)}</td>
                                        </tr>
                                    `}).join('')}
                                </tbody>
                            </table>
                        `;

                        const isDisabled = row.enabled === false;
                        const badgeBackground = isDisabled 
                            ? 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)' 
                            : 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)';
                        const badgeBorderColor = isDisabled ? '#BDBDBD' : '#0C4F93';
                        const badgeOpacity = isDisabled ? '0.7' : '1';
                        const iconColor = isDisabled ? '#757575' : '#0C4F93';
                        const countBackground = isDisabled ? '#9E9E9E' : '#0C4F93';

                        return `
                            <div class="conditions-wrapper" style="position: relative; display: inline-block;">
                                <span 
                                    class="conditions-badge"
                                    style="
                                        background: ${badgeBackground};
                                        color: #01172F;
                                        border: 1px solid ${badgeBorderColor};
                                        opacity: ${badgeOpacity};
                                        border-radius: 16px;
                                        padding: 2px 8px;
                                        display: inline-flex;
                                        align-items: center;
                                        gap: 4px;
                                        font-size: 11px;
                                        cursor: pointer;
                                        box-shadow: 0 2px 4px rgba(33, 150, 243, 0.15);
                                    "
                                    onmouseenter="
                                        const tooltip = this.nextElementSibling;
                                        const rect = this.getBoundingClientRect();
                                        const spaceBelow = window.innerHeight - rect.bottom;
                                        const tooltipHeight = 300;
                                        
                                        tooltip.style.right = (window.innerWidth - rect.right) + 'px';
                                        
                                        if (spaceBelow < tooltipHeight && rect.top > tooltipHeight) {
                                            tooltip.style.top = 'auto';
                                            tooltip.style.bottom = (window.innerHeight - rect.top + 8) + 'px';
                                        } else {
                                            tooltip.style.top = (rect.bottom + 8) + 'px';
                                            tooltip.style.bottom = 'auto';
                                        }
                                    "
                                >
                                    <i class="pi pi-filter" style="font-size: 12px; color:${iconColor};"></i>
                                    <span style="color: #01172F;">${t('conditions')}</span>
                                    <span style="background: ${countBackground}; color: #fff; border-radius: 12px; font-weight: 700; font-size: 10px; min-width: 20px; text-align: center;">${filteredConditions.length}</span>
                                </span>
                                <div 
                                    class="conditions-tooltip"
                                    style="
                                        visibility: hidden;
                                        opacity: 0;
                                        position: fixed;
                                        z-index: 1000;
                                        background-color: white;
                                        border: 1px solid #ddd;
                                        border-radius: 8px;
                                        overflow: hidden;
                                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                                        width: 550px;
                                        transition: opacity 0.2s, visibility 0.2s;
                                        pointer-events: none;
                                    "
                                >
                                    ${tooltipTable}
                                </div>
                            </div>
                            <style>
                                .conditions-badge:hover + .conditions-tooltip {
                                    visibility: visible !important;
                                    opacity: 1 !important;
                                }
                            </style>
                        `;
                    },
                    width: "17%",
                },
                {
                    label: () => t('action').toUpperCase(),
                    field: "commands",
                    justify: 'justify-content-center',
                    style: { justifyItems: 'center' },
                    components: [
                        {
                            component: "appGroupTableEditComponent",
                        },
                        {
                            component: "copyAction",
                            class: "mr-2 ml-2",
                        },
                        {
                            component: "appGroupDeleteComponent",
                        },
                    ],
                    width: "120px",
                },
            ],
            rows: [],
            total: 0,
            sortable: {
                order: "",
                sort: "asc",
            },
            page: 0,
            pageSize: 0,
            isLoading: true,
            showPagination: false,
            dateFilter: false,
            zebra: true,
            separateBorders: true,
            editOnRowClick: true,
            fetchTable: (options: any) => {
                store.dispatch(GeneralActionTypes.GET_SUMMARY_EVENTS_LIST, {
                    options: options,
                    filters: {}
                });
            },
        },
    },

};

export default data;
